<script setup lang="ts">
import {
  computed,
  defineComponent,
  h,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  NxConfirmHost,
  NxFab,
  NxLiveAnnouncer,
  NxToastHost,
  useNxCommands,
  useNxToasts,
  type NxFabAction,
} from "@blpde/nx-ui";
import { ChatIcon, SparkleIcon } from "@blpde/nx-icons";
import { assistantChannelKey, type AgentRecordContext } from "@blpde/connect";
import ShellNav from "@/components/ShellNav.vue";
import ModuleGate from "@/components/ModuleGate.vue";
import AssistantPanel from "@/components/AssistantPanel.vue";
import ChatPanel from "@/components/ChatPanel.vue";
import VoicePanel from "@/components/VoicePanel.vue";
import TileWorkspaces from "@/tiles/TileWorkspaces.vue";
import { useTilesEnabled } from "@/tiles/tileNav";
import {
  answerTileAssistantContext,
  recordContextOf,
  requestActiveTileContext,
} from "@/tiles/tileAssistantContext";
import { connectTileId } from "@blpde/connect/config";
import ShellImpersonationBanner from "@/components/ShellImpersonationBanner.vue";
import ShellSupportBanner from "@/components/ShellSupportBanner.vue";
import SupportApprovalDialog from "@/components/SupportApprovalDialog.vue";
import { useShellUser } from "@/auth";
import { useShellAssistant } from "@/assistant/useShellAssistant";
import { useSearchSettings } from "@/search/settings";
import { useShellChat } from "@/chat/useShellChat";
import { useSupportRequests } from "@/support/useSupportRequests";
import { useShellVoice } from "@/voice/useShellVoice";
import { useAccountApplications } from "@/applications";
import { appForPath, type AppId } from "@/integration";
import { useShellAppCommands } from "@/integration/shellAppCommands";
import { useModuleAvailability } from "@/modules";
import { useSystemNotifications } from "@/systemNotifications";
import {
  installChatFormicaBridge,
  registerChatMessengerHandler,
} from "@/services/chatService";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAvailable, loaded } = useModuleAvailability();
const { isEntitled } = useAccountApplications();
const routeReady = ref(false);
/** The row under the surface where the workspaces put their tab bar. */
const tileBar = ref<HTMLElement | null>(null);
const { user: shellUser } = useShellUser();
// A tile of a workspace (I-3641) is a whole Connect in an iframe: the page around
// it owns the workspaces and the shell-wide things that must exist only once.
const inTile = connectTileId() !== null;
// The browser's system notifications live as long as the shell: the navbar is
// gone on routes without chrome (the layout designer), reminders still come.
if (!inTile) useSystemNotifications();
/**
 * A banner takes a grid row of its own between nav and surface: the
 * impersonation banner, the support operator's banner of a remote support
 * session (Fernwartung) — both `ShellImpersonationBanner` — or, for the
 * installation's admins, the banner of a support session that runs.
 */
// The approval dialog and the admins' banner exist once, in the page around the tiles.
const supportSession = inTile ? null : useSupportRequests().active;
const impersonating = computed(
  () => !!shellUser.value?.impersonation || !!shellUser.value?.support,
);
const bannered = computed(() => impersonating.value || !!supportSession?.value);

const assistantChannel = inject(assistantChannelKey, null);
const { showToast } = useNxToasts();
const {
  openAssistant,
  send: sendToAssistant,
  conversation,
} = useShellAssistant();
const { openChat } = useShellChat();
const { openVoice } = useShellVoice();

/** App + backend module pair for a FAB action (mirrors AppRail gates). */
const fabAppGate: Record<
  "chat" | "voice" | "assistant",
  { appId: AppId; moduleKey: string }
> = {
  chat: { appId: "chat", moduleKey: "chat" },
  // Voice + Assistent both ride the agent module / app entitlement.
  voice: { appId: "agent", moduleKey: "agent" },
  assistant: { appId: "agent", moduleKey: "agent" },
};

function canUseFabApp(appId: AppId, moduleKey: string): boolean {
  return isEntitled(appId) && isAvailable(moduleKey);
}

/** false → toast already shown (no access / offline). true → proceed. */
function ensureFabAccess(
  key: "chat" | "voice" | "assistant",
  actionLabel: string,
): boolean {
  const gate = fabAppGate[key];
  if (!isEntitled(gate.appId)) {
    showToast(t("connect.rail.noAccess", { name: actionLabel }), "info");
    return false;
  }
  if (!isAvailable(gate.moduleKey)) {
    showToast(t("connect.rail.unavailable", { name: actionLabel }), "info");
    return false;
  }
  return true;
}

const VoiceGlyph = defineComponent({
  name: "VoiceGlyph",
  // Sized like nx-icons: 1em attribute default, so a `nxt-icon-*` class wins;
  // an explicit `size` prop still sets inline width/height.
  props: { size: { type: String, default: undefined } },
  setup(props) {
    return () =>
      h(
        "svg",
        {
          class: "nx-icon",
          viewBox: "0 0 24 24",
          width: "1em",
          height: "1em",
          style: props.size
            ? { width: props.size, height: props.size }
            : undefined,
          fill: "none",
          stroke: "currentColor",
          "stroke-width": 2,
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
          "aria-hidden": "true",
        },
        [
          h("rect", { x: 9, y: 3, width: 6, height: 11, rx: 3 }),
          h("path", { d: "M5 11a7 7 0 0 0 14 0" }),
          h("path", { d: "M12 18v3" }),
          h("path", { d: "M8 21h8" }),
        ],
      );
  },
});

// Start chrome-less until the router resolved the target route; otherwise
// chrome-less tools like the layout designer can flash the shell navbar.
// Not tied to the chrome: a route without it must not end the tiles and their work.
const tilesEnabled = useTilesEnabled();
// Switched off, the workspaces stay until their tiles agreed to go: closing asks.
const tilesHeld = ref(tilesEnabled.value);
watch(tilesEnabled, async (enabled) => {
  if (!enabled) return;
  // Switched on: this page moves into a tab too (the router's guard does that),
  // beside the stored tabs, which come in when the workspaces mount.
  if (routeReady.value)
    await router
      .replace({
        path: route.path,
        query: route.query,
        hash: route.hash,
        force: true,
      })
      .catch(() => undefined);
  tilesHeld.value = true;
});
async function releaseTiles(page: string): Promise<void> {
  // A page that does not load: the start page takes over (below).
  await router.replace(page).catch(() => undefined);
  tilesHeld.value = false;
}
const hostsWorkspaces = computed(
  () => !inTile && tilesHeld.value && routeReady.value && !!shellUser.value,
);
// Switched off and its tiles gone: `/tiles` has nothing to show.
watch(
  () => !tilesEnabled.value && !tilesHeld.value && route.path === "/tiles",
  (stranded) => {
    if (stranded) void router.replace("/");
  },
  { immediate: true },
);
const showChrome = computed(
  () =>
    routeReady.value &&
    !route.meta?.public &&
    !route.meta?.hideShellChrome &&
    route.name !== "ConnectLogin",
);

// FAB is shell chrome: one global instance for every hosted app (incl. ERP).
// Optional context claimants (B-DMS, later others) claim via assistantChannel.
// Actions without app entitlement / online module are omitted entirely.
const fabActions = computed<NxFabAction[]>(() => {
  const actions: NxFabAction[] = [];
  if (canUseFabApp("chat", "chat")) {
    actions.push({
      key: "chat",
      label: t("connect.fab.chat"),
      icon: ChatIcon,
    });
  }
  if (canUseFabApp("agent", "agent")) {
    actions.push(
      {
        key: "voice",
        label: t("connect.fab.voice"),
        icon: VoiceGlyph,
      },
      {
        key: "assistant",
        label: t("connect.fab.assistant"),
        icon: SparkleIcon,
      },
    );
  }
  return actions;
});

// Hide the whole FAB when chrome is off or nothing is entitled/online.
const showFab = computed(
  () => !inTile && showChrome.value && fabActions.value.length > 0,
);

/**
 * The record the surface in front of the user claims. On the workspaces page it
 * lives in the active tile's own Connect, not in this page's channel: ask the
 * tile (`tiles/tileAssistantContext.ts`); no answer is no record.
 */
function currentRecord(): Promise<AgentRecordContext | null> {
  if (!inTile && route.path === "/tiles") return requestActiveTileContext();
  return Promise.resolve(recordContextOf(ownClaim()));
}

function ownClaim(): AgentRecordContext | null | undefined {
  return assistantChannel?.request()?.context;
}

// A tile answers the page's question for its own claimant (B-DMS).
let stopAnswering: () => void = () => {};
if (inTile) stopAnswering = answerTileAssistantContext(ownClaim);

function onFabSelect(key: string) {
  if (key !== "chat" && key !== "voice" && key !== "assistant") return;

  const label =
    key === "chat"
      ? t("connect.fab.chat")
      : key === "voice"
        ? t("connect.fab.voice")
        : t("connect.fab.assistant");

  if (!ensureFabAccess(key, label)) return;

  if (key === "assistant") {
    // Shell-owned support panel: new chat with default agent, optional
    // polymorphic context from a mounted claimant (B-DMS, later others).
    // No agent/model picker — defaults only. Full app stays at /agent.
    void currentRecord().then(openAssistant);
    return;
  }
  if (key === "chat") {
    // Shell DM panel: pick coworker, reuse existing direct channel if any.
    void openChat();
    return;
  }
  if (key === "voice") {
    // Push-to-talk → WAV STT → default agent conversation (background chat).
    void currentRecord().then(openVoice);
    return;
  }
}

const activeApp = computed(() => appForPath(route.path));

// Gate the surface when the active app's master module isn't reachable — this
// also protects direct URL entry (the app component is never mounted). Only
// once availability has actually loaded (isAvailable is optimistic until then).
// erp's own verdict pages stay visible: the router guard sends users to
// service-unavailable exactly when erp is down, so gating it would mask the
// page in its primary scenario (BLP/nx#1139).
const gated = computed(
  () =>
    showChrome.value &&
    !!activeApp.value &&
    route.name !== "erp.PermissionDenied" &&
    route.name !== "erp.ServiceUnavailable" &&
    loaded.value &&
    !isAvailable(activeApp.value.moduleKey),
);
const checkingModule = computed(
  () =>
    showChrome.value &&
    !!activeApp.value &&
    activeApp.value.moduleKey !== "connect" &&
    !loaded.value,
);
const gateLabel = computed(() =>
  activeApp.value ? t(activeApp.value.labelKey) : "",
);

// Hosted modules (ERP task AccountChip, etc.) cannot import connect source —
// they call window.formica.openChat(...). Bridge that into the shell ChatPanel.
let stopChatBridge: (() => void) | null = null;

onMounted(() => {
  stopChatBridge = registerChatMessengerHandler((intent) => {
    void openChat({
      accountId: intent.accountId,
      name: intent.name,
      email: intent.email,
    });
  });
  installChatFormicaBridge();

  // Also when the first page failed (`loadFailure.ts`): the shell stands, not a white page.
  const showShell = () => {
    routeReady.value = true;
  };
  void router.isReady().then(showShell, showShell);
});

// „Assistent fragen: <Eingabe>“ — the palette's last entry when nothing
// matched (I-2348). Registered here, not in App.vue: the assistant needs the
// shell socket, which App.vue provides to its descendants only.
const paletteRegistry = useNxCommands();
const { settings: searchSettings } = useSearchSettings();
let unregisterAskAssistant: () => void = () => {};
const stopAskAssistantWatch = watch(
  () => searchSettings.value.askAssistant && canUseFabApp("agent", "agent"),
  (enabled) => {
    unregisterAskAssistant();
    unregisterAskAssistant = () => {};
    if (!enabled || !paletteRegistry) return;
    unregisterAskAssistant = paletteRegistry.registerFallback({
      id: "connect.askAssistant",
      label: (query) => t("connect.search.askAssistant", { query }),
      icon: SparkleIcon,
      run: async (query) => {
        await openAssistant(null);
        if (conversation.value) await sendToAssistant(query);
      },
    });
  },
  { immediate: true },
);

// „Neue Nachricht an …“ and „Assistent öffnen“ (I-2348): the chat's and the
// agent's palette entries whose action is one of the shell's panels.
useShellAppCommands({
  canUseChat: () => canUseFabApp("chat", "chat"),
  canUseAssistant: () => canUseFabApp("agent", "agent"),
  openChatWith: (account) => {
    if (!ensureFabAccess("chat", t("connect.fab.chat"))) return;
    void openChat({
      accountId: account.id,
      name: account.name,
      email: account.email,
    });
  },
  openAssistant: () => onFabSelect("assistant"),
});

onBeforeUnmount(() => {
  stopAnswering();
  stopChatBridge?.();
  stopChatBridge = null;
  stopAskAssistantWatch();
  unregisterAskAssistant();
});
</script>

<template>
  <div
    class="cx-shell"
    :class="{
      'no-chrome': !showChrome,
      'is-impersonating': showChrome && bannered,
    }"
  >
    <ShellNav v-if="showChrome" />
    <ShellImpersonationBanner v-if="showChrome && impersonating" />
    <ShellSupportBanner v-else-if="showChrome && !inTile" />
    <SupportApprovalDialog v-if="showChrome && !inTile" />
    <!-- The one shell-wide toast host, plus useNxConfirm()/useNxPrompt()
         dialogs and useNxAnnounce() live regions for every hosted app. All
         three stay mounted on chrome-less routes too (layout designer,
         login): a toast or confirmation raised there must still show, and
         each renders nothing until used. -->
    <NxToastHost placement="bottom-center" />
    <NxConfirmHost />
    <NxLiveAnnouncer />
    <main class="cx-surface" data-nx-scope="content">
      <div v-if="checkingModule" class="cx-module-check">
        {{ t("connect.gate.checking") }}
      </div>
      <ModuleGate v-else-if="gated" :app-label="gateLabel" />
      <slot v-else />
      <TileWorkspaces
        v-if="hostsWorkspaces"
        :bar-target="tileBar"
        @released="releaseTiles"
      />
    </main>
    <div
      v-if="hostsWorkspaces"
      v-show="showChrome"
      ref="tileBar"
      class="cx-tile-bar"
      :class="{ 'has-fab': showFab }"
    />
    <AssistantPanel />
    <ChatPanel />
    <VoicePanel />
    <NxFab
      v-if="showFab"
      :actions="fabActions"
      :label="t('connect.fab.label')"
      position="bottom-right"
      :draggable="true"
      storage-key="connect.fab"
      @select="onFabSelect"
    />
  </div>
</template>

<style scoped>
.cx-tile-bar {
  display: flex;
  min-width: 0;
}
/* The floating button's usual corner: the bar's end stays reachable beside it. */
.cx-tile-bar.has-fab::after {
  content: "";
  flex: none;
  width: calc(var(--nxt-size-unit) * 5);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-bg);
}

.cx-shell.no-chrome {
  grid-template-rows: 1fr;
}

.cx-shell.is-impersonating {
  grid-template-rows: auto auto minmax(0, 1fr) auto;
}

.cx-module-check {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
</style>
